// Copyright (C) Siemens AG 2026. All rights reserved. Confidential
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MontlyCostsExport } from 'src/app/model/costexplorer.model';
import { AppEnvironmentService } from '../app-environment.service';

export interface TenantOrder {
    id: number;
    name: string;
    emailOfOrderer: string;
    orderDate: string;
    billingType: string;
    lifecycleState: string;
    isSubtenant: boolean;
}

export interface CreditKeyOrder {
    id: number;
    orderRef: string;
    orderSource: string;
    orderCoin: number;
    orderPrice: number;
    orderDate: string;
    orderExpiresAt: string | null;
    expiredAt: string | null;
    confirmedAt: string | null;
    ordererEmail: string;
    assignmentDate: string | null;
    assignedTenantId: number | null;
    remainingCredits: number;
    remainingCreditsModifiedAt: string | null;
}

export interface TopUp {
    id: number;
    assignmentDate: string;
    creditsAdded: number;
    remainingCredits: number;
    addedBy: string;
    expiredAt: string | null;
}

export interface CreditLimitChange {
    initialCreditLimit: number;
    changedAt: string | null;
    changedByEmail: string;
}

export interface MyOrdersResponse {
    tenantOrders: TenantOrder[];
    creditKeyOrders: CreditKeyOrder[];
}

const JSON_CONTENT_TYPE = 'application/json';

@Injectable({ providedIn: 'root' })
export class CreditService {

    private get baseApiUrl(): string { return this.appEnvService.baseApiUrl; }

    constructor(private readonly http: HttpClient, private readonly appEnvService: AppEnvironmentService) {

    }

    getTenantCosts(tenantId: number, month: number, year: number): Observable<MontlyCostsExport> {
        let accessToken;
        accessToken = localStorage.getItem('access_token')
        const getCreditsUrl = this.baseApiUrl + 'projectcredits';


        const headers = new HttpHeaders({
            'Content-Type': JSON_CONTENT_TYPE,
            'Authorization': `Bearer ${accessToken}`
        })

        const params = new HttpParams().set('tenant_id', tenantId.toString()).set('month', month).set('year', year);

        const options = {
            params: params,
            headers: headers,
        }

        return this.http.get<MontlyCostsExport>(getCreditsUrl, options).pipe(catchError((error: any) => {
            throw (error)
        }));

    }

    getSimitLibUsageCurrentDay(projectId: number): Observable<any> {
        let accessToken;
        accessToken = localStorage.getItem('access_token');
        const getSimitLibUsageUrl = this.baseApiUrl + 'projects/' + projectId + '/simitcosts';

        const headers = new HttpHeaders({
            'Content-Type': JSON_CONTENT_TYPE,
            'Authorization': `Bearer ${accessToken}`
        })

        const options = {
            headers: headers,
            timeout: 10000 
        }

        return this.http.get<any>(getSimitLibUsageUrl, options).pipe(catchError((error: any) => {
            throw (error)
        }))
    }

    redeemKey(tenantId: number, key: string): Observable<{ message: string; creditsAdded: number; expiresAt: string }> {
        const accessToken = localStorage.getItem('access_token');
        const url = this.baseApiUrl + 'creditkey/redeem';
        const headers = new HttpHeaders({
            'Content-Type': JSON_CONTENT_TYPE,
            'Authorization': `Bearer ${accessToken}`
        });
        return this.http.post<{ message: string; creditsAdded: number; expiresAt: string }>(
            url, { tenantId, key }, { headers }
        ).pipe(catchError((error: any) => { throw error; }));
    }

    getMyOrders(): Observable<MyOrdersResponse> {
        const accessToken = localStorage.getItem('access_token');
        const url = this.baseApiUrl + 'myOrders';
        const headers = new HttpHeaders({
            'Content-Type': JSON_CONTENT_TYPE,
            'Authorization': `Bearer ${accessToken}`
        });
        return this.http.get<MyOrdersResponse>(url, { headers }).pipe(
            catchError((error: any) => { throw error; })
        );
    }

    getTopUps(tenantId: number): Observable<TopUp[]> {
        const accessToken = localStorage.getItem('access_token');
        const url = this.baseApiUrl + `tenants/${tenantId}/topups`;
        const headers = new HttpHeaders({
            'Content-Type': JSON_CONTENT_TYPE,
            'Authorization': `Bearer ${accessToken}`
        });
        return this.http.get<TopUp[]>(url, { headers }).pipe(
            catchError((error: any) => { throw error; })
        );
    }

    getCreditLimitChanges(tenantId: number): Observable<CreditLimitChange[]> {
        const accessToken = localStorage.getItem('access_token');
        const url = this.baseApiUrl + `tenants/${tenantId}/credit-limits`;
        const headers = new HttpHeaders({
            'Content-Type': JSON_CONTENT_TYPE,
            'Authorization': `Bearer ${accessToken}`
        });
        return this.http.get<CreditLimitChange[]>(url, { headers }).pipe(
            catchError((error: any) => { throw error; })
        );
    }
}



